import { For, Show, type JSX } from "solid-js";
import { Icon } from "~/icon";

type HeaderProps = {
  locationName: string;
  logoUrl: string | null;
  addressLines: string[] | null;
  phone: string | null;
};

function LocationHeader(props: HeaderProps) {
  return (
    <div class="flex items-start justify-between gap-4 border-b border-brand-subtle bg-brand-light px-6 py-5 sm:px-10">
      <div class="min-w-0">
        <p class="truncate font-medium">{props.locationName}</p>
        <div class="mt-1 text-sm text-typography-secondary">
          <Show when={props.addressLines}>
            {(lines) => <For each={lines()}>{(line) => <p class="truncate">{line}</p>}</For>}
          </Show>
          <Show when={props.phone}>{(phone) => <p>{phone()}</p>}</Show>
        </div>
      </div>

      <Show when={props.logoUrl}>
        {(url) => (
          <img
            src={url()}
            alt={props.locationName}
            class="h-16 w-auto shrink-0 rounded-lg object-contain"
          />
        )}
      </Show>
    </div>
  );
}

/** Centered check + heading + body, shared by the opt-in confirmation and opt-out pages. */
export function PublicConfirmation(props: { title: string; children: JSX.Element }) {
  return (
    <div class="flex flex-col items-center py-6 text-center">
      <div class="flex size-12 items-center justify-center rounded-full bg-brand-light text-brand-primary">
        <Icon name="check" class="size-6" />
      </div>
      <h1 class="mt-4 text-2xl font-semibold">{props.title}</h1>
      <p class="mt-2 text-sm text-typography-secondary">{props.children}</p>
    </div>
  );
}

export function LocationPanel(props: HeaderProps & { children: JSX.Element }) {
  return (
    <div class="overflow-hidden rounded-2xl bg-white shadow-lg ring-1 shadow-black/5 ring-black/5">
      <LocationHeader
        locationName={props.locationName}
        logoUrl={props.logoUrl}
        addressLines={props.addressLines}
        phone={props.phone}
      />
      <div class="px-6 py-8 sm:px-10 sm:py-10">{props.children}</div>
    </div>
  );
}

export function PublicPageShell(props: { maxWidth?: "3xl" | "4xl"; children: JSX.Element }) {
  return (
    <div class="flex min-h-dvh flex-col items-center px-4 py-6 sm:py-12">
      <div class={`w-full ${props.maxWidth === "4xl" ? "max-w-4xl" : "max-w-3xl"}`}>
        {props.children}
      </div>
    </div>
  );
}

/** `toolbar` sits above the card (the patient pages' language picker). */
export function PublicLocationCard(
  props: HeaderProps & { maxWidth?: "3xl" | "4xl"; toolbar?: JSX.Element; children: JSX.Element },
) {
  return (
    <PublicPageShell maxWidth={props.maxWidth}>
      {props.toolbar}
      <LocationPanel {...props} />
    </PublicPageShell>
  );
}
